import { CircularProgress, SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useLazyDropDownListQuery } from '~/api/oneBoxV3';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import { dropdown } from '~/components/Global/ResourceSearch/ResourceSearch.style';
import {
  getEduCode,
  sortGrade,
  sortHighSchoolGrade,
  sortSchoolYearsNewestFirstReadable,
  subjectsSort,
} from '~/components/Global/ResourceSearch/resourceSearchSortHelpers';
import { SearchCategory } from '~/types/enum';
import { getResourceSearchHeader } from '~/utils/constants';
import type { TSearchCondition } from '~/store/resource/resource.type';

/**
 * 後台 /latestnews 篩選列：
 * - 排版／配色與前台 ResourceSearch（image 2）一致（黃色邊框下拉）。
 * - 不寫入 Redux／localStorage，避免污染前台資源頁狀態。
 * - 級聯規則同前台：學制 → 科目 → 學年度／年級。
 * - 額外加入「全部」選項，方便後台清除個別欄位。
 */

export const LATEST_NEWS_ADMIN_FILTER_ALL = '全部';

export type TLatestNewsAdminFilterValue = {
  /** '全部' 或空字串視為不篩選 */
  eduCode: string;
  subject: string;
  schoolYear: string;
  grade: string;
};

export const emptyLatestNewsAdminFilter = (): TLatestNewsAdminFilterValue => ({
  eduCode: LATEST_NEWS_ADMIN_FILTER_ALL,
  subject: LATEST_NEWS_ADMIN_FILTER_ALL,
  schoolYear: LATEST_NEWS_ADMIN_FILTER_ALL,
  grade: LATEST_NEWS_ADMIN_FILTER_ALL,
});

export const isLatestNewsAdminFilterAll = (v: string | undefined | null) =>
  !v || v === LATEST_NEWS_ADMIN_FILTER_ALL;

type TProps = {
  value: TLatestNewsAdminFilterValue;
  onChange: (next: TLatestNewsAdminFilterValue) => void;
};

const EDU_CODE_OPTIONS: string[] = [
  resourceEduCode.E,
  resourceEduCode.J,
  resourceEduCode.H,
];

export const LatestNewsAdminFilter = ({ value, onChange }: TProps) => {
  const { eduCode, subject, schoolYear, grade } = value;

  const [fetchSubjects, { data: subjectData, isFetching: isFetchingSubjects }] =
    useLazyDropDownListQuery();
  const [fetchYears, { data: yearsData, isFetching: isFetchingYears }] =
    useLazyDropDownListQuery();
  const [fetchGrades, { data: gradesData, isFetching: isFetchingGrades }] =
    useLazyDropDownListQuery();

  // 取得科目下拉
  useEffect(() => {
    if (isLatestNewsAdminFilterAll(eduCode)) return;
    fetchSubjects({
      isLaunch: true,
      categories: [SearchCategory.SCHOOL],
      resourceProps: ['subjects'],
      search: { eduCodes: eduCode },
    }).catch(e => {
      console.error('Failed to fetch admin subjects dropdown:', e);
    });
  }, [eduCode, fetchSubjects]);

  // 取得學年度下拉（需學制+科目）
  useEffect(() => {
    if (
      isLatestNewsAdminFilterAll(eduCode) ||
      isLatestNewsAdminFilterAll(subject)
    ) {
      return;
    }
    fetchYears({
      isLaunch: true,
      categories: [SearchCategory.SCHOOL],
      resourceProps: ['years'],
      search: {
        eduCodes: eduCode,
        subjects: subject,
        grades: isLatestNewsAdminFilterAll(grade) ? undefined : grade,
      },
    }).catch(e => {
      console.error('Failed to fetch admin years dropdown:', e);
    });
  }, [eduCode, subject, grade, fetchYears]);

  // 取得年級下拉（需學制+科目）
  useEffect(() => {
    if (
      isLatestNewsAdminFilterAll(eduCode) ||
      isLatestNewsAdminFilterAll(subject)
    ) {
      return;
    }
    fetchGrades({
      isLaunch: true,
      categories: [SearchCategory.SCHOOL],
      resourceProps: ['grades'],
      search: {
        eduCodes: eduCode,
        subjects: subject,
        years: isLatestNewsAdminFilterAll(schoolYear) ? undefined : schoolYear,
      },
    }).catch(e => {
      console.error('Failed to fetch admin grades dropdown:', e);
    });
  }, [eduCode, subject, schoolYear, fetchGrades]);

  const subjectOptions = useMemo(
    () =>
      subjectsSort(
        subjectData?.data?.uniqResourceProps?.subjects || [],
        getEduCode(eduCode),
      ),
    [subjectData, eduCode],
  );

  const yearOptions = useMemo(
    () =>
      sortSchoolYearsNewestFirstReadable(
        yearsData?.data?.uniqResourceProps?.years || [],
      ),
    [yearsData],
  );

  const gradeOptions = useMemo(() => {
    const raw = gradesData?.data?.uniqResourceProps?.grades || [];
    if (eduCode === (resourceEduCode.H as string)) {
      return sortHighSchoolGrade(subject || '', raw);
    }
    return sortGrade(raw) || [];
  }, [gradesData, eduCode, subject]);

  const handleChange = (id: string, raw: unknown) => {
    const v = (raw as string) ?? '';
    let next: TLatestNewsAdminFilterValue;
    switch (id) {
      case 'eduCodes':
        next = {
          eduCode: v,
          subject: LATEST_NEWS_ADMIN_FILTER_ALL,
          schoolYear: LATEST_NEWS_ADMIN_FILTER_ALL,
          grade: LATEST_NEWS_ADMIN_FILTER_ALL,
        };
        break;
      case 'subjects':
        next = {
          eduCode,
          subject: v,
          schoolYear: LATEST_NEWS_ADMIN_FILTER_ALL,
          grade: LATEST_NEWS_ADMIN_FILTER_ALL,
        };
        break;
      case 'years':
        next = { eduCode, subject, schoolYear: v, grade };
        break;
      case 'grades':
        next = { eduCode, subject, schoolYear, grade: v };
        break;
      default:
        return;
    }
    onChange(next);
  };

  const filters = [
    {
      id: 'eduCodes' as const,
      value: eduCode || LATEST_NEWS_ADMIN_FILTER_ALL,
      options: [LATEST_NEWS_ADMIN_FILTER_ALL, ...EDU_CODE_OPTIONS],
      disabled: false,
      isFetching: false,
    },
    {
      id: 'subjects' as const,
      value: subject || LATEST_NEWS_ADMIN_FILTER_ALL,
      options: [LATEST_NEWS_ADMIN_FILTER_ALL, ...subjectOptions],
      disabled: isLatestNewsAdminFilterAll(eduCode),
      isFetching: isFetchingSubjects,
    },
    {
      id: 'years' as const,
      value: schoolYear || LATEST_NEWS_ADMIN_FILTER_ALL,
      options: [LATEST_NEWS_ADMIN_FILTER_ALL, ...yearOptions],
      disabled:
        isLatestNewsAdminFilterAll(eduCode) ||
        isLatestNewsAdminFilterAll(subject),
      isFetching: isFetchingYears,
    },
    {
      id: 'grades' as const,
      value: grade || LATEST_NEWS_ADMIN_FILTER_ALL,
      options: [LATEST_NEWS_ADMIN_FILTER_ALL, ...gradeOptions],
      disabled:
        isLatestNewsAdminFilterAll(eduCode) ||
        isLatestNewsAdminFilterAll(subject),
      isFetching: isFetchingGrades,
    },
  ];

  return (
    <dropdown.Container>
      {filters.map(({ id, value: v, options, disabled, isFetching }) => (
        <dropdown.FormControl key={id}>
          <dropdown.Label disabled={disabled}>
            {getResourceSearchHeader(id as keyof TSearchCondition)}
          </dropdown.Label>
          <dropdown.Select
            value={v}
            label={getResourceSearchHeader(id as keyof TSearchCondition)}
            name={id}
            disabled={disabled}
            onChange={(event: SelectChangeEvent<unknown>) => {
              const { name, value: nextValue } = event.target;
              handleChange(name, nextValue);
            }}
            MenuProps={{
              PaperProps: {
                style: { maxHeight: '50vh' },
              },
            }}
          >
            {isFetching ? (
              <dropdown.Item disabled>
                <CircularProgress sx={{ color: '#00A15D' }} size={24} />
              </dropdown.Item>
            ) : (
              options.map(option => (
                <dropdown.Item value={option} key={option}>
                  {option}
                </dropdown.Item>
              ))
            )}
          </dropdown.Select>
        </dropdown.FormControl>
      ))}
    </dropdown.Container>
  );
};
